import { useCallback, useState } from "react";
import { classifyEnrollError } from "./enrollContract";
import { runEnrollment, STEP, enrollSiweSupported } from "./enrollFlow";

/**
 * useEnrollment — React state around the shared enrollment sequence
 * (onboarding step 2).
 *
 *   idle → nonce → signing → submitting → enrolled
 *                                       ↘ error (classified, see enrollContract)
 *
 * The sequence itself lives in enrollFlow.js and is reached only through this
 * hook, which the Strategy Automation tab uses (ARC-52). This hook only owns
 * state + error classification; it makes no network call and shapes no request
 * body.
 *
 * It never falls back to the pre-SIWE single-call enroll: that request gets a
 * 400 from the live engine, and pretending otherwise would show members a
 * failure we could have predicted. If signalsApi lacks the SIWE client the hook
 * reports itself unsupported and sends nothing.
 *
 * The wallet signature is obtained through the caller-supplied `signMessage`
 * (useArbitrumWallet). No key material passes through here.
 */

// Re-exported so existing importers (tests) keep one import site.
export { STEP, enrollSiweSupported };

export default function useEnrollment({ wallet, signMessage } = {}) {
  const [step, setStep] = useState(STEP.IDLE);
  const [enrollment, setEnrollment] = useState(null);
  const [error, setError] = useState(null);
  const [challenge, setChallenge] = useState(null);

  const supported = enrollSiweSupported();

  const reset = useCallback(() => {
    setStep(STEP.IDLE);
    setError(null);
    setChallenge(null);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    try {
      const res = await runEnrollment({
        wallet,
        signMessage,
        onStep: setStep,
        onChallenge: setChallenge,
      });
      setEnrollment(res);
      setStep(STEP.ENROLLED);
      return true;
    } catch (e) {
      setStep(STEP.ERROR);
      setError(classifyEnrollError(e));
      return false;
    }
  }, [wallet, signMessage]);

  return {
    supported,
    step,
    busy: step === STEP.NONCE || step === STEP.SIGNING || step === STEP.SUBMITTING,
    enrolled: step === STEP.ENROLLED,
    enrollment,
    error,
    challenge,
    start,
    reset,
  };
}
